import { lazy, Suspense, useEffect, useState } from 'react';
import { useT } from '../i18n';
import { useGame } from '../store';
import { LOGO_URL } from './art';
import { Icon } from './icons';
import { LegalPage, LegalWindow } from './LegalWindow';
import { MainMenu } from './MainMenu';
import { Paywall } from './Paywall';

/*
 * The title screen loads first and alone; the game (three.js, the simulation's views), the
 * model gallery and the Academy come in their own files when first wanted, so the menu is up
 * quickly even on a slow connection. The game's file is fetched in the background as soon as the
 * menu is shown, so starting a game does not wait for it.
 */
const loadGame = () => import('./GameScreen');
const GameScreen = lazy(() => loadGame().then((m) => ({ default: m.GameScreen })));
const Gallery = lazy(() => import('./Gallery').then((m) => ({ default: m.Gallery })));
const Academy = lazy(() => import('./Academy').then((m) => ({ default: m.Academy })));

const galleryMode = new URLSearchParams(location.search).has('gallery');
/** The legal texts as pages of their own, for the stores (/privacy, /terms). */
const legalPath = /^\/(privacy|gizlilik)\/?$/.test(location.pathname)
  ? 'privacy'
  : /^\/(terms|kosullar)\/?$/.test(location.pathname)
    ? 'terms'
    : null;

export function App() {
  const screen = useGame((s) => s.screen);
  const startMode = useGame((s) => s.startMode);
  const helpOpen = useGame((s) => s.helpOpen);
  // The Academy stays mounted once opened (it keeps its page and scroll between visits).
  const [academy, setAcademy] = useState(false);
  useEffect(() => {
    if (helpOpen) setAcademy(true);
  }, [helpOpen]);
  useEffect(() => {
    const idle = window.requestIdleCallback ?? ((f: () => void) => window.setTimeout(f, 1500));
    idle(() => void loadGame());
  }, []);
  if (legalPath) return <LegalPage doc={legalPath} />;
  if (galleryMode) {
    return (
      <Suspense fallback={null}>
        <Gallery />
      </Suspense>
    );
  }
  return (
    <>
      {screen === 'game' && startMode ? (
        <Suspense fallback={<div className="game-loading-blank" />}>
          <GameScreen mode={startMode} />
        </Suspense>
      ) : (
        <MainMenu />
      )}
      {academy && (
        <Suspense fallback={null}>
          <Academy />
        </Suspense>
      )}
      <LegalWindow />
      <Paywall />
      <RotateHint />
    </>
  );
}

/**
 * A phone or tablet held upright (mobile.css shows it then): the game is played on its side,
 * so it asks to be turned.
 */
function RotateHint() {
  const t = useT();
  return (
    <div className="rotate-hint" aria-hidden>
      <img className="rotate-logo" src={LOGO_URL} alt="" draggable={false} />
      <Icon name="phone" size={54} />
      {t('hud.rotatePhone')}
    </div>
  );
}
